import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Fab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProfessionalModal from '../professionalModal';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import AddIcon from '@mui/icons-material/Add';
import { useProfessionalsList } from '../hooks/useProfessionalsList';
import { useDeleteProfessional } from '../hooks/useDeleteProfessional';
import { useRooms } from '../../../hooks/useRooms';
import { useRoomLabel } from '../../../hooks/useRoomLabel';
import { DigitalCertificateStatus, WorkingHoursSummary } from '../components/professionalDetails';

function ProfessionalListMobile() {
  const navigate = useNavigate();

  const {
    professionals,
    isLoading,
    error: listError,
    refetch: getProfessionals,
  } = useProfessionalsList();
  const { remove } = useDeleteProfessional();
  const { rooms } = useRooms();
  const roomLabel = useRoomLabel();

  const getRoomName = (roomId) => rooms.find((room) => room.id === roomId)?.name ?? '—';

  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, professionalId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProfessionalId(professionalId);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const editProfessional = () => {
    const professionalToEdit = professionals.find((d) => d.id === selectedProfessionalId);
    if (professionalToEdit) {
      setSelectedProfessional(professionalToEdit);
      setModalOpen(true);
    }
    closeMenu();
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedProfessionalId)
      .then((returnFromApi) => {
        getProfessionals();
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('Erro ao excluir o profissional!', returnFromApi);
        if (returnFromApi.response?.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => {
        setSelectedProfessionalId(null);
      });
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedProfessionalId(null);
  };

  const openCreateModal = () => {
    setSelectedProfessional(null);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setSelectedProfessional(null);
  };

  function saveProfessional(returnFromApi) {
    getProfessionals();
    closeModal();
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
  }

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 2 }}>
        <Card sx={{ borderRadius: '16px', boxShadow: 'none' }}>
          <CardHeader
            title="Profissionais"
            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: 'text.secondary' }}
          />
          <CardContent>
            <List>
              {professionals.length === 0 && (
                <ListItem>
                  <ListItemText primary="Nenhum profissional encontrado" />
                </ListItem>
              )}
              {professionals.map((professional) => (
                <React.Fragment key={professional.id}>
                  <ListItem
                    data-cy="dentist-row"
                    secondaryAction={
                      <IconButton
                        data-cy="dentist-actions-btn"
                        onClick={(event) => clickMenu(event, professional.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={professional.name}
                      primaryTypographyProps={{ fontWeight: 600 }}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {professional.croNumber && (
                            <Typography variant="body2" color="text.secondary">
                              CRO-{professional.croUf} {professional.croNumber}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {roomLabel.singular}: {getRoomName(professional.roomId)}
                          </Typography>
                          <WorkingHoursSummary workingHours={professional.workingHours} />
                          <div>
                            <DigitalCertificateStatus
                              fileName={professional.digitalCertificateFileName}
                              uploadedAt={professional.digitalCertificateUploadedAt}
                              label="certificado digital"
                            />
                          </div>
                        </Stack>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Fab
        data-cy="new-dentist-btn"
        color="primary"
        aria-label="adicionar profissional"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={openCreateModal}
      >
        <AddIcon />
      </Fab>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
        <MenuItem onClick={editProfessional}>Editar Profissional</MenuItem>
        <MenuItem
          data-cy="delete-dentist-btn"
          onClick={openConfirmModal}
          sx={{ color: 'error.main' }}
        >
          Excluir
        </MenuItem>
      </Menu>

      <ProfessionalModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={saveProfessional}
        professional={selectedProfessional}
      />

      <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
        <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold' }}>
          {'Confirmar Exclusão'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Você tem certeza que deseja excluir este profissional? Essa ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmModal}>Cancelar</Button>
          <Button
            data-cy="confirm-delete-dentist-btn"
            onClick={confirmDelete}
            variant="contained"
            color="error"
            autoFocus
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProfessionalListMobile;
